// idb-backed offline mutation queue
import { openDB, type DBSchema, type IDBPDatabase } from 'idb'

interface OfflineMutation {
  id?: number
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  path: string
  body?: unknown
  meta?: Record<string, string>
  failCount?: number
  timestamp: number
}

interface MiseDB extends DBSchema {
  'offline-queue': {
    key: number
    value: OfflineMutation
  }
}

let db: IDBPDatabase<MiseDB> | null = null

async function getDb(): Promise<IDBPDatabase<MiseDB>> {
  if (!db) {
    db = await openDB<MiseDB>('mise-offline', 1, {
      upgrade(database) {
        database.createObjectStore('offline-queue', { keyPath: 'id', autoIncrement: true })
      },
    })
  }
  return db
}

export const offlineQueue = {
  async enqueue(mutation: Omit<OfflineMutation, 'id' | 'timestamp'>): Promise<void> {
    const database = await getDb()
    await database.add('offline-queue', { ...mutation, timestamp: Date.now() })
  },

  async dequeue(): Promise<OfflineMutation[]> {
    const database = await getDb()
    return database.getAll('offline-queue')
  },

  async remove(id: number): Promise<void> {
    const database = await getDb()
    await database.delete('offline-queue', id)
  },

  async update(id: number, patch: Partial<Omit<OfflineMutation, 'id'>>): Promise<void> {
    const database = await getDb()
    const existing = await database.get('offline-queue', id)
    if (existing) {
      await database.put('offline-queue', { ...existing, ...patch })
    }
  },

  async clear(): Promise<void> {
    const database = await getDb()
    await database.clear('offline-queue')
  },
}
